import { createManualEntry } from "@packages/api-client/client/financialHubController";
import { apiClientFetch } from "@packages/api-client/client-config";
import {
	getFinancialTransactionQueryKey,
	listFinancialTransactionsQueryKey,
	useCreateManualEntry as useCreateManualEntryBase,
	useCreateTransactionFromReceipt as useCreateTransactionFromReceiptBase,
	useDeleteManualEntry as useDeleteManualEntryBase,
	useReviewFinancialTransactions as useReviewFinancialTransactionsBase,
	useUpdateManualEntry as useUpdateManualEntryBase,
} from "@packages/api-client/hooks/financialHubController";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { OFFLINE_WRITE } from "@/lib/offline/capabilities";
import { defineOfflineMutation } from "@/lib/offline/defineOfflineMutation";
import {
	getOnboardingSnapshotQueryKey,
	reconcileOnboardingAfterFirstRecord,
	reconcileOnboardingAfterReplay,
} from "../onboarding";

// ─── Re-exports for consumers ──────────────────────────────────────────────

export {
	getFinancialTransactionQueryOptions,
	listFinancialTransactionsQueryOptions,
} from "@packages/api-client/hooks/financialHubController";
export type {
	CreateManualEntryMutationRequest,
	ListFinancialTransactions200,
} from "@packages/api-client/types/financialHubController";

// ─── Offline-capable writes ──────────────────────────────────────────────────

// The quick "Lançamento" capture (mobile FAB) queues offline: firing while
// unreachable enqueues `variables` verbatim and the central MutationCache
// replays it FIFO on reconnect, producing an identical manual ledger row.
// Registered at module scope so replays survive a page refresh.
const manualEntryCreateOffline = defineOfflineMutation({
	key: OFFLINE_WRITE.manualEntryCreate,
	payloadVersion: 1,
	replay: ({
		organizationId,
		data,
	}: {
		organizationId: string;
		data: Parameters<typeof createManualEntry>[1];
	}) => createManualEntry(organizationId, data, { client: apiClientFetch }),
	// Replay-time success effect (D4): the per-hook onSuccess below can't run on
	// replay, so the list refresh is registered here, derived from payload alone.
	// The onboarding snapshot rides along: a manual entry is one of the five
	// qualifying first records, so a replay can move FIRST_RECORD from the
	// client-side "queued" presentation to a genuine server-derived DONE (§12).
	invalidate: ({ organizationId }) => [
		listFinancialTransactionsQueryKey(organizationId),
		getOnboardingSnapshotQueryKey(organizationId),
	],
	// Materialize activation from the record's own timestamp rather than waiting
	// for the hourly backstop. Never enqueues a completion claim — the endpoint
	// takes no domain facts from the browser (§9.3, §12).
	onReplaySuccess: ({ organizationId }) =>
		reconcileOnboardingAfterReplay(organizationId),
});

// ─── Mutation Hooks ─────────────────────────────────────────────────────────

export const useCreateManualEntry = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useCreateManualEntryBase({
		mutation: {
			// offlineFirst: the mutation fires even when offline so the central
			// MutationCache.onError can enqueue it (see manualEntryCreateOffline).
			networkMode: manualEntryCreateOffline.networkMode,
			mutationFn: manualEntryCreateOffline.mutationFn,
			meta: {
				...manualEntryCreateOffline.meta,
				toastMessages: {
					loading: t("common.creating"),
					success: t("common.createSuccess"),
				},
			},
			onSuccess: (data, variables) => {
				queryClient.invalidateQueries({
					queryKey: listFinancialTransactionsQueryKey(variables.organizationId),
				});
				// A manual entry is one of the five qualifying first records, so the
				// onboarding snapshot goes stale with the transactions list (§16).
				reconcileOnboardingAfterFirstRecord(
					queryClient,
					variables.organizationId,
					data,
				);
			},
		},
	});
};

/**
 * Receipt-OCR confirm (NDC-108): creates an UNREVIEWED manual entry from the
 * reviewed extraction and re-points the OCR'd draft document onto it. Online-only
 * (the whole scan flow requires the network), so unlike the plain manual entry it
 * carries no offline-queue wiring.
 */
export const useCreateTransactionFromReceipt = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useCreateTransactionFromReceiptBase({
		mutation: {
			meta: {
				toastMessages: {
					loading: t("common.creating"),
					success: t("common.createSuccess"),
				},
			},
			onSuccess: (_data, variables) => {
				queryClient.invalidateQueries({
					queryKey: listFinancialTransactionsQueryKey(variables.organizationId),
				});
			},
		},
	});
};

export const useUpdateManualEntry = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useUpdateManualEntryBase({
		mutation: {
			meta: {
				toastMessages: {
					loading: t("common.updating"),
					success: t("common.updateSuccess"),
				},
			},
			onSuccess: (_data, variables) => {
				queryClient.invalidateQueries({
					queryKey: listFinancialTransactionsQueryKey(variables.organizationId),
				});
				queryClient.invalidateQueries({
					queryKey: getFinancialTransactionQueryKey(
						variables.organizationId,
						variables.transactionId,
					),
				});
			},
		},
	});
};

export const useDeleteManualEntry = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useDeleteManualEntryBase({
		mutation: {
			meta: {
				toastMessages: {
					loading: t("common.deleting"),
					success: t("common.deleteSuccess"),
				},
			},
			onSuccess: (_data, variables) => {
				queryClient.invalidateQueries({
					queryKey: listFinancialTransactionsQueryKey(variables.organizationId),
				});
				queryClient.removeQueries({
					queryKey: getFinancialTransactionQueryKey(
						variables.organizationId,
						variables.transactionId,
					),
				});
			},
		},
	});
};

export const useReviewFinancialTransactions = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useReviewFinancialTransactionsBase({
		mutation: {
			meta: {
				toastMessages: {
					loading: t("common.saving"),
					success: t("financial.transactions.review.reviewSuccess"),
				},
			},
			onSuccess: (_data, variables) => {
				queryClient.invalidateQueries({
					queryKey: listFinancialTransactionsQueryKey(variables.organizationId),
				});
				// Refresh each reviewed transaction's detail query so a detail-page
				// review doesn't leave the "A revisar" badge/controls stale (BUG-2).
				for (const transactionId of variables.data.transactionIds) {
					queryClient.invalidateQueries({
						queryKey: getFinancialTransactionQueryKey(
							variables.organizationId,
							transactionId,
						),
					});
				}
				// The overview counter and P&L / cash-flow reports are derived from
				// these transactions; invalidate every cached period so their numbers
				// refresh after a review (BUG-2).
				queryClient.invalidateQueries({
					predicate: (query) => {
						const head = query.queryKey[0];
						return (
							typeof head === "object" &&
							head !== null &&
							"url" in head &&
							typeof (head as { url: unknown }).url === "string" &&
							(head as { url: string }).url.includes("/financial/reports/")
						);
					},
				});
			},
		},
	});
};
